import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Shield, Plus, Trash2, Search, ArrowLeft, Music2, Image as ImageIcon, Users, Swords, Crown, Upload, Wand2, Loader2, Smartphone, TrendingUp, Link as LinkIcon, Copy, UserPlus, Star, Inbox } from "lucide-react";
import ExerciseRequestsAdmin from "../components/ExerciseRequestsAdmin";
import { toast } from "sonner";

const COMMUNITY_TYPES = [
  { id: "volume", label: "Total Volume Lifted (kg)" },
  { id: "workouts", label: "Workouts Completed" },
  { id: "reps", label: "Reps on Specific Exercise" },
  { id: "bodyweight_loss", label: "Body Weight Lost (kg)" },
  { id: "cardio_distance", label: "Cardio Distance (km)" },
  { id: "cardio_duration", label: "Cardio Time (minutes)" },
];

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = !!user?.is_admin;
  if (user && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <button onClick={() => nav(-1)} className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest mb-3">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>
      <div className="flex items-center gap-2 text-blue-500 font-bold text-xs tracking-[0.3em] uppercase">
        <Shield className="w-3 h-3" /> ADMIN ONLY
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mt-1 mb-5">Control Room</h1>

      <Tabs defaultValue="challenges">
        <TabsList className="bg-neutral-900 border border-neutral-800 rounded-none h-auto p-0 flex-wrap">
          <TabsTrigger value="challenges" data-testid="admin-tab-challenges" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Swords className="w-3 h-3 mr-1.5" /> Challenges</TabsTrigger>
          <TabsTrigger value="tiktok" data-testid="admin-tab-tiktok" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Music2 className="w-3 h-3 mr-1.5" /> TikTok</TabsTrigger>
          <TabsTrigger value="photos" data-testid="admin-tab-photos" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><ImageIcon className="w-3 h-3 mr-1.5" /> Photos</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Users className="w-3 h-3 mr-1.5" /> Users</TabsTrigger>
          <TabsTrigger value="admins" data-testid="admin-tab-admins" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Star className="w-3 h-3 mr-1.5" /> Admins</TabsTrigger>
          <TabsTrigger value="requests" data-testid="admin-tab-requests" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Inbox className="w-3 h-3 mr-1.5" /> Requests</TabsTrigger>
          <TabsTrigger value="thumbs" data-testid="admin-tab-thumbs" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Wand2 className="w-3 h-3 mr-1.5" /> Thumbs</TabsTrigger>
          <TabsTrigger value="install" data-testid="admin-tab-install" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Smartphone className="w-3 h-3 mr-1.5" /> Install</TabsTrigger>
        </TabsList>

        <TabsContent value="challenges" className="mt-5"><CommunityChallengesAdmin /></TabsContent>
        <TabsContent value="tiktok" className="mt-5"><HighlightsAdmin /></TabsContent>
        <TabsContent value="photos" className="mt-5"><TransformationsAdmin /></TabsContent>
        <TabsContent value="users" className="mt-5"><UsersAdmin /></TabsContent>
        <TabsContent value="admins" className="mt-5"><AdminsAdmin currentUserId={user.user_id} /></TabsContent>
        <TabsContent value="requests" className="mt-5"><ExerciseRequestsAdmin /></TabsContent>
        <TabsContent value="thumbs" className="mt-5"><ThumbnailsAdmin /></TabsContent>
        <TabsContent value="install" className="mt-5"><InstallAnalyticsAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Community Challenges ----------
function CommunityChallengesAdmin() {
  const [items, setItems] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", challenge_type: "volume", target_value: "", duration_days: 14, exercise_id: "", exercise_name: "" });

  const load = async () => {
    const [c, e] = await Promise.all([api.get("/community-challenges"), api.get("/exercises")]);
    setItems(c.data); setExercises(e.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title required"); return; }
    try {
      await api.post("/admin/community-challenges", {
        title: form.title,
        description: form.description,
        challenge_type: form.challenge_type,
        target_value: Number(form.target_value) || 0,
        exercise_id: form.challenge_type === "reps" ? form.exercise_id : null,
        exercise_name: form.challenge_type === "reps" ? form.exercise_name : null,
        duration_days: Number(form.duration_days) || 14,
      });
      toast.success("Challenge launched");
      setOpen(false);
      setForm({ title: "", description: "", challenge_type: "volume", target_value: "", duration_days: 14, exercise_id: "", exercise_name: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete this challenge?")) return;
    await api.delete(`/admin/community-challenges/${cid}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-neutral-400">Community challenges any user can join</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-new-challenge-btn" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9"><Plus className="w-3 h-3 mr-1" /> New</Button>
          </DialogTrigger>
          <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
            <DialogHeader><DialogTitle className="font-display text-2xl uppercase">New Community Challenge</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Title</Label>
                <Input data-testid="admin-cc-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="e.g. 100 Push-Ups in 7 Days" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Description</Label>
                <Textarea data-testid="admin-cc-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Type</Label>
                  <Select value={form.challenge_type} onValueChange={(v) => setForm({...form, challenge_type: v})}>
                    <SelectTrigger data-testid="admin-cc-type" className="mt-2 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800">
                      {COMMUNITY_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Duration (days)</Label>
                  <Input data-testid="admin-cc-days" type="number" min={1} max={90} value={form.duration_days} onChange={(e) => setForm({...form, duration_days: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
                </div>
              </div>
              {form.challenge_type === "reps" && (
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Exercise</Label>
                  <Select value={form.exercise_id} onValueChange={(v) => {
                    const ex = exercises.find((e) => e.exercise_id === v);
                    setForm({...form, exercise_id: v, exercise_name: ex?.name || ""});
                  }}>
                    <SelectTrigger data-testid="admin-cc-exercise" className="mt-2 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue placeholder="Pick exercise" /></SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800 max-h-60">
                      {exercises.map((e) => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Target value (optional)</Label>
                <Input data-testid="admin-cc-target" type="number" min={0} value={form.target_value} onChange={(e) => setForm({...form, target_value: e.target.value})} placeholder="100" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <Button data-testid="admin-cc-submit" type="submit" className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">Launch Challenge</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-400">No community challenges yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((ch) => (
            <div key={ch.challenge_id} data-testid={`admin-cc-row-${ch.challenge_id}`} className="border border-neutral-800 p-3 flex items-center gap-3">
              <div className="flex-1">
                <div className="font-display text-lg uppercase">{ch.title}</div>
                <div className="text-xs text-neutral-500">{ch.challenge_type} · {ch.duration_days}d · {ch.participant_count || 0} joined · <span className={ch.status === "active" ? "text-blue-500" : "text-neutral-500"}>{ch.status}</span></div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => remove(ch.challenge_id)} className="text-neutral-400 hover:text-blue-500 h-8 w-8"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Highlights (Coach TikToks) ----------
function HighlightsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tiktok_url: "", title: "", description: "" });

  const load = async () => { const { data } = await api.get("/coach/highlights"); setItems(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tiktok_url.includes("tiktok.com")) { toast.error("Paste a TikTok URL"); return; }
    try {
      await api.post("/coach/highlights", form);
      setOpen(false); setForm({ tiktok_url: "", title: "", description: "" }); load();
      toast.success("Pinned");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove?")) return;
    await api.delete(`/coach/highlights/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-neutral-400">Pin TikTok videos to the Coach Hub</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-new-highlight" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9"><Plus className="w-3 h-3 mr-1" /> Pin</Button>
          </DialogTrigger>
          <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
            <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Pin TikTok</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">TikTok URL</Label>
                <Input data-testid="admin-hl-url" value={form.tiktok_url} onChange={(e) => setForm({...form, tiktok_url: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Title</Label>
                <Input data-testid="admin-hl-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Description</Label>
                <Textarea data-testid="admin-hl-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none" rows={2} />
              </div>
              <Button data-testid="admin-hl-submit" type="submit" className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">Pin</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-400">No highlights yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((h) => (
            <div key={h.highlight_id} className="border border-neutral-800 p-3 flex items-center gap-3" data-testid={`admin-hl-row-${h.highlight_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg uppercase truncate">{h.title || "Untitled"}</div>
                <a href={h.tiktok_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline truncate block">{h.tiktok_url}</a>
              </div>
              <Button size="icon" variant="ghost" onClick={() => remove(h.highlight_id)} className="text-neutral-400 hover:text-blue-500 h-8 w-8"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Transformation Photos ----------
function TransformationsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ image_data_url: "", caption: "", kind: "progress", weight_kg: "", sort_order: 0 });
  const [uploading, setUploading] = useState(false);

  const load = async () => { const { data } = await api.get("/coach/transformations"); setItems(data); };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Max 4 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, image_data_url: reader.result }));
    reader.readAsDataURL(f);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_data_url) { toast.error("Pick an image or paste a URL"); return; }
    setUploading(true);
    try {
      await api.post("/coach/transformations", { ...form, weight_kg: form.weight_kg ? Number(form.weight_kg) : null, sort_order: Number(form.sort_order) || 0 });
      toast.success("Added"); setOpen(false);
      setForm({ image_data_url: "", caption: "", kind: "progress", weight_kg: "", sort_order: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setUploading(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove?")) return;
    await api.delete(`/coach/transformations/${id}`); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-neutral-400">Before/after & progress photos</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-new-photo" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9"><Upload className="w-3 h-3 mr-1" /> Upload</Button>
          </DialogTrigger>
          <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
            <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Add Photo</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Image URL or upload (max 4MB)</Label>
                <Input data-testid="admin-tr-url" value={form.image_data_url.startsWith("http") ? form.image_data_url : ""} onChange={(e) => setForm({...form, image_data_url: e.target.value})} placeholder="https://..." className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
                <input data-testid="admin-tr-file" type="file" accept="image/*" onChange={onFile} className="mt-2 block w-full text-sm text-neutral-300 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-blue-500 file:text-white file:uppercase file:tracking-wider file:font-display file:text-xs" />
                {form.image_data_url && <img src={form.image_data_url} alt="" className="mt-3 max-h-40 mx-auto" />}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Kind</Label>
                  <select data-testid="admin-tr-kind" value={form.kind} onChange={(e) => setForm({...form, kind: e.target.value})} className="mt-2 w-full bg-neutral-900 border border-neutral-800 text-white rounded-none h-11 px-3">
                    <option value="before">Before</option>
                    <option value="progress">Progress</option>
                    <option value="after">After</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Weight (kg)</Label>
                  <Input data-testid="admin-tr-weight" type="number" value={form.weight_kg} onChange={(e) => setForm({...form, weight_kg: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Caption</Label>
                <Input data-testid="admin-tr-caption" value={form.caption} onChange={(e) => setForm({...form, caption: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Sort order</Label>
                <Input data-testid="admin-tr-order" type="number" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <Button data-testid="admin-tr-submit" type="submit" disabled={uploading} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">{uploading ? "Uploading..." : "Save"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-400">No photos yet.</div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {items.map((t) => (
            <div key={t.transformation_id} className="border border-neutral-800 relative" data-testid={`admin-tr-row-${t.transformation_id}`}>
              <img src={t.image_data_url} alt="" className="w-full aspect-square object-cover" />
              <div className="absolute top-1 left-1 bg-black/80 px-1 text-[9px] uppercase tracking-widest text-blue-500">{t.kind}</div>
              <Button size="icon" variant="ghost" onClick={() => remove(t.transformation_id)} className="absolute bottom-1 right-1 bg-black/80 text-neutral-400 hover:text-blue-500 h-6 w-6"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Users / Scholarships ----------
function UsersAdmin() {
  // Live-search & browse: empty query -> shows most recent signups; typing filters.
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [scholarships, setScholarships] = useState([]);

  // Quick-grant by email (no need to scroll the list)
  const [quickEmail, setQuickEmail] = useState("");
  const [quickBusy, setQuickBusy] = useState(false);

  const loadSch = async () => { const { data } = await api.get("/admin/scholarships"); setScholarships(data); };
  useEffect(() => { loadSch(); }, []);

  // Auto-load + debounced live filter as admin types
  useEffect(() => {
    const handle = setTimeout(async () => {
      setUsersLoading(true);
      try {
        const { data } = await api.get("/admin/users", { params: { q, limit: 30 } });
        setUsers(data);
      } catch (e) {
        // ignore — keep prior list
      } finally { setUsersLoading(false); }
    }, q ? 250 : 0);
    return () => clearTimeout(handle);
  }, [q]);

  const toggle = async (uid, on, email) => {
    // optimistic update
    setUsers((prev) => prev.map((u) => u.user_id === uid ? { ...u, is_scholarship: on } : u));
    try {
      await api.post("/admin/scholarship", { user_id: uid, on });
      toast.success(on ? `Sponsored ${email || "user"}` : "Sponsorship revoked");
      loadSch();
    } catch (e) {
      // revert on failure
      setUsers((prev) => prev.map((u) => u.user_id === uid ? { ...u, is_scholarship: !on } : u));
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const quickGrant = async () => {
    if (!quickEmail.trim()) return;
    setQuickBusy(true);
    try {
      await api.post("/admin/scholarship/quick-grant", { email: quickEmail.trim(), on: true });
      toast.success(`Sponsored ${quickEmail.trim()}`);
      setQuickEmail("");
      loadSch();
      // refresh the visible list so the new sponsor shows up
      const { data } = await api.get("/admin/users", { params: { q, limit: 30 } });
      setUsers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setQuickBusy(false); }
  };

  const fmtJoined = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));
      if (days < 1) return "today";
      if (days < 2) return "yesterday";
      if (days < 30) return `${days}d ago`;
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <div>
      {/* Quick-grant card */}
      <div data-testid="quick-grant-card" className="border border-blue-500/40 bg-blue-500/5 p-3 mb-4">
        <div className="flex items-center gap-2 text-blue-500 text-xs tracking-[0.3em] uppercase font-bold mb-2">
          <Crown className="w-3 h-3" /> QUICK SPONSOR
        </div>
        <p className="text-xs text-neutral-300 mb-2">Know their email? One tap.</p>
        <div className="flex gap-2">
          <Input
            data-testid="quick-grant-email"
            value={quickEmail}
            onChange={(e) => setQuickEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), quickGrant())}
            placeholder="email@example.com"
            type="email"
            className="bg-neutral-900 border-neutral-800 text-white rounded-none h-10"
          />
          <Button data-testid="quick-grant-btn" onClick={quickGrant} disabled={quickBusy || !quickEmail} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10 shrink-0">
            {quickBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Sponsor"}
          </Button>
        </div>
        <p className="text-[10px] text-neutral-500 mt-1.5 uppercase tracking-widest">Recipient must already have an account. For pre-signups, use Invite Links below.</p>
      </div>

      {/* Browse / live-filter recent signups */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-neutral-400 flex items-center gap-2">
          <Users className="w-3 h-3 text-blue-500" /> {q ? `Matches (${users.length})` : `Recent signups (${users.length})`}
        </div>
        {usersLoading && <Loader2 className="w-3 h-3 animate-spin text-neutral-500" />}
      </div>
      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <Input
          data-testid="admin-user-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name or email…"
          className="bg-neutral-900 border-neutral-800 text-white rounded-none h-10 pl-9"
        />
      </div>

      {users.length === 0 && !usersLoading ? (
        <div className="border border-dashed border-neutral-800 p-6 text-xs text-neutral-400 text-center mb-4">
          {q ? "No users match that filter." : "No users yet."}
        </div>
      ) : (
        <div className="divide-y divide-neutral-800 border border-neutral-800 mb-4">
          {users.map((u) => (
            <div key={u.user_id} className="p-3 flex items-center justify-between gap-2" data-testid={`admin-user-${u.user_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">
                  {u.name || u.email}
                  {u.is_scholarship && (
                    <span data-testid="sponsored-badge" className="text-[9px] uppercase tracking-widest bg-blue-500 text-white px-1.5 py-0.5 flex items-center gap-1">
                      <Crown className="w-2.5 h-2.5" /> Sponsored
                    </span>
                  )}
                  {!u.is_scholarship && u.is_pro && (
                    <span className="text-[9px] uppercase tracking-widest bg-neutral-800 text-neutral-300 px-1.5 py-0.5">Pro</span>
                  )}
                </div>
                <div className="text-xs text-neutral-500 truncate">{u.email}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-0.5">
                  Joined {fmtJoined(u.created_at)}
                </div>
              </div>
              <Button
                size="sm"
                data-testid={`admin-toggle-${u.user_id}`}
                onClick={() => toggle(u.user_id, !u.is_scholarship, u.email)}
                disabled={u.is_super_admin}
                className={
                  u.is_scholarship
                    ? "bg-transparent border border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-8 shrink-0"
                    : "bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8 shrink-0"
                }
              >
                {u.is_scholarship ? "Revoke" : "Sponsor"}
              </Button>
            </div>
          ))}
        </div>
      )}

      {scholarships.length > 0 && (
        <>
          <div className="text-xs uppercase tracking-widest text-neutral-400 mb-2 flex items-center gap-2"><Crown className="w-3 h-3 text-blue-500" /> All active sponsorships ({scholarships.length})</div>
          <div className="divide-y divide-neutral-800 border border-neutral-800">
            {scholarships.map((u) => (
              <div key={u.user_id} className="p-2 flex items-center justify-between text-sm">
                <span className="truncate">{u.email}</span>
                {u.email !== "admin@gainstracker.com" && (
                  <Button size="sm" variant="ghost" onClick={() => toggle(u.user_id, false, u.email)} className="text-neutral-400 hover:text-blue-500 uppercase tracking-wider h-7">Revoke</Button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <ScholarshipInvitesSection />
    </div>
  );
}

// ---------- Scholarship invite links ----------
function ScholarshipInvitesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [duration, setDuration] = useState(0);
  const [expires, setExpires] = useState(30);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/scholarship-invites");
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/admin/scholarship-invites", {
        label, duration_days: Number(duration) || 0, expires_in_days: Number(expires) || 30,
      });
      toast.success("Invite link created");
      setLabel(""); setDuration(0); setExpires(30); setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this invite? The link will stop working.")) return;
    await api.delete(`/admin/scholarship-invites/${token}`);
    toast.success("Revoked");
    load();
  };

  const copy = async (item) => {
    const url = `${window.location.origin}${item.url}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy — long-press to copy"); }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-neutral-400 flex items-center gap-2">
          <LinkIcon className="w-3 h-3 text-blue-500" /> Invite links ({items.length})
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-invite-btn" size="sm" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8">
              <Plus className="w-3 h-3 mr-1" /> New Invite
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-neutral-950 border-neutral-800 text-white">
            <DialogHeader><DialogTitle className="font-display text-2xl uppercase">New Invite Link</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Label (your reference)</Label>
                <Input data-testid="invite-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. TikTok winner #3" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Pro duration (days)</Label>
                  <Input data-testid="invite-duration" type="number" min={0} value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="0 = lifetime" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
                  <p className="text-[10px] text-neutral-500 mt-1 uppercase tracking-widest">0 = lifetime scholarship</p>
                </div>
                <div>
                  <Label className="text-xs tracking-widest uppercase text-neutral-400">Link expires in</Label>
                  <Input data-testid="invite-expires" type="number" min={1} value={expires} onChange={(e) => setExpires(e.target.value)} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
                  <p className="text-[10px] text-neutral-500 mt-1 uppercase tracking-widest">Days until link stops working</p>
                </div>
              </div>
              <Button data-testid="create-invite-btn" onClick={create} disabled={creating} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">
                {creating ? "Creating…" : "Generate link"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-neutral-400 flex items-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-4 text-xs text-neutral-400 text-center">
          No invite links yet. Create one to gift Pro to anyone — even before they sign up.
        </div>
      ) : (
        <div className="divide-y divide-neutral-800 border border-neutral-800">
          {items.map((it) => {
            const expired = new Date(it.expires_at) < new Date();
            return (
              <div key={it.token} data-testid={`invite-row-${it.token}`} className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm uppercase truncate">{it.label}</div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">
                      {it.duration_days > 0 ? `${it.duration_days} days Pro` : "Lifetime"} · expires {new Date(it.expires_at).toLocaleDateString()}
                      {expired && <span className="text-blue-500"> · EXPIRED</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => copy(it)} className="text-neutral-400 hover:text-blue-500 h-8 w-8" title="Copy link">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => revoke(it.token)} className="text-neutral-400 hover:text-blue-500 h-8 w-8" title="Revoke">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {it.claimed_by_email ? (
                  <div className="text-[10px] text-blue-500 uppercase tracking-widest mt-1">
                    ✓ Claimed by {it.claimed_by_email}
                  </div>
                ) : (
                  <div className="font-mono-d text-[10px] text-neutral-400 mt-1 truncate">/claim/{it.token}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ThumbnailsAdmin() {
  const [progress, setProgress] = useState({ running: false, done: 0, total: 0, skipped: 0, failed: 0 });
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState({ total: 0, with_thumb: 0 });

  const refresh = async () => {
    try {
      const [{ data: prog }, { data: exs }] = await Promise.all([
        api.get("/admin/exercises/thumbnail-progress"),
        api.get("/exercises"),
      ]);
      setProgress(prog);
      setStats({ total: exs.length, with_thumb: exs.filter((e) => !!e.thumbnail_data_url).length });
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, []);

  const start = async (force = false) => {
    setBusy(true);
    try {
      await api.post(`/admin/exercises/regenerate-thumbnails${force ? "?force=true" : ""}`);
      toast.success("Started — refreshes every 4s");
      setTimeout(refresh, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start");
    } finally {
      setBusy(false);
    }
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="border border-neutral-800 p-5">
        <h3 className="font-display text-2xl uppercase mb-1">Library Thumbnails</h3>
        <div className="text-xs text-neutral-400">
          Pre-generates a 3D clip-art thumbnail for every exercise via Gemini Nano Banana. Runs in the background.
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="border border-neutral-800 p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Library</div>
            <div className="font-display text-3xl">{stats.with_thumb}<span className="text-neutral-500 text-base"> / {stats.total}</span></div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider">have thumbnails</div>
          </div>
          <div className="border border-neutral-800 p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Job Status</div>
            <div className="font-display text-3xl">{progress.running ? <Loader2 className="inline w-6 h-6 animate-spin" /> : "Idle"}</div>
            {progress.running && <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{progress.done}/{progress.total} ({pct}%)</div>}
          </div>
        </div>
        {progress.running && (
          <div className="mt-3 h-2 bg-neutral-900 border border-neutral-800">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <Button data-testid="thumbs-start-btn" onClick={() => start(false)} disabled={busy || progress.running} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display">
            <Wand2 className="w-4 h-4 mr-2" /> Generate missing
          </Button>
          <Button data-testid="thumbs-force-btn" onClick={() => start(true)} disabled={busy || progress.running} variant="outline" className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display">
            Rebuild all
          </Button>
        </div>
        <div className="text-[10px] text-neutral-500 uppercase tracking-wider mt-3">
          ⚠ Each thumbnail uses a Gemini image-gen call — costs LLM key budget. ~68 images × small fee.
        </div>
      </div>
    </div>
  );
}


// ---------- Install Analytics ----------
function InstallAnalyticsAdmin() {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get("/admin/analytics/install"),
        api.get("/install-config"),
      ]);
      setData(a.data);
      setConfig(c.data);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-neutral-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (err) return <div className="text-sm text-blue-500">{err}</div>;
  if (!data) return null;

  const ic = data.cohorts.installed;
  const bc = data.cohorts.browser_only;

  return (
    <div data-testid="install-analytics" className="space-y-4">
      {/* Headline */}
      <div className="border border-neutral-800 p-5">
        <div className="text-xs tracking-[0.2em] uppercase text-neutral-400 font-bold mb-1">PWA INSTALL ANALYTICS</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <Stat label="Total users" value={data.total_users} />
          <Stat label="Installed" value={data.total_installed} accent />
          <Stat label="Install rate" value={`${data.install_rate_pct}%`} />
          <Stat label="New (14d)" value={data.installs_14d} />
        </div>
      </div>

      {/* Cohort comparison */}
      <div className="border border-neutral-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl uppercase">Retention Lift</h3>
          {data.retention_lift_x != null && (
            <div data-testid="retention-lift" className="px-2 py-1 bg-blue-500 text-white text-[10px] font-display tracking-widest uppercase flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> {data.retention_lift_x}× more retentive
            </div>
          )}
        </div>
        <p className="text-xs text-neutral-400 mb-4">Installed (PWA) cohort vs. browser-only cohort, last 30 days.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <CohortCard data-testid="cohort-installed" name="Installed (PWA)" cohort={ic} accent />
          <CohortCard data-testid="cohort-browser" name="Browser only" cohort={bc} />
        </div>
      </div>

      {/* Adaptive nudge config */}
      {config && (
        <div data-testid="adaptive-nudge-card" className="border border-neutral-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl uppercase">Adaptive Nudge</h3>
            <div data-testid="nudge-threshold-badge" className="px-2 py-1 bg-blue-500 text-white text-[10px] font-display tracking-widest uppercase">
              Threshold: {config.nudge_threshold} {config.nudge_threshold === 1 ? "workout" : "workouts"}
            </div>
          </div>
          <p className="text-xs text-neutral-400 mb-2">Self-tuning growth loop — when installed users retain better, the streak toast appears sooner. Computed from live retention-lift data; cached {config.ttl_seconds}s server-side.</p>
          <div className="text-xs text-neutral-300 italic">"{config.reason}"</div>
        </div>
      )}

      <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Generated {new Date(data.generated_at).toLocaleString()}</div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-blue-500/50 bg-blue-500/5" : "border-neutral-800"} p-3`}>
      <div className="text-[10px] tracking-widest uppercase text-neutral-500">{label}</div>
      <div className={`font-display text-3xl mt-1 ${accent ? "text-blue-500" : "text-white"}`}>{value}</div>
    </div>
  );
}

function CohortCard({ name, cohort, accent, ...rest }) {
  return (
    <div {...rest} className={`border ${accent ? "border-blue-500/50 bg-blue-500/5" : "border-neutral-800"} p-4`}>
      <div className={`font-display text-lg uppercase ${accent ? "text-blue-500" : "text-white"} mb-3`}>{name}</div>
      <Row label="Users" v={cohort.users} />
      <Row label="Active in 30d" v={`${cohort.active_30d} (${cohort.active_30d_pct}%)`} />
      <Row label="Avg sessions" v={cohort.avg_sessions} />
      <Row label="Median sessions" v={cohort.median_sessions} />
      <Row label="Avg sessions / 30d" v={cohort.avg_sessions_30d} />
    </div>
  );
}
function Row({ label, v }) {
  return (
    <div className="flex items-baseline justify-between text-sm py-1 border-b border-neutral-900 last:border-0">
      <span className="text-neutral-400 text-xs uppercase tracking-widest">{label}</span>
      <span className="font-display tabular-nums text-white">{v}</span>
    </div>
  );
}


// ---------- Admin role management ----------
function AdminsAdmin({ currentUserId }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const promote = async (e) => {
    if (e) e.preventDefault();
    const target = email.trim();
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/admins", { email: target });
      if (data?.already_admin) {
        toast.info(`${target} is already an admin`);
      } else {
        toast.success(`${target} promoted to admin`);
      }
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't promote");
    } finally { setBusy(false); }
  };

  const revoke = async (a) => {
    if (!window.confirm(`Remove admin access from ${a.email}?`)) return;
    try {
      await api.delete(`/admin/admins/${a.user_id}`);
      toast.success("Admin removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove");
    }
  };

  return (
    <div data-testid="admins-admin">
      {/* Promote card */}
      <div data-testid="promote-admin-card" className="border border-blue-500/40 bg-blue-500/5 p-3 mb-4">
        <div className="flex items-center gap-2 text-blue-500 text-xs tracking-[0.3em] uppercase font-bold mb-2">
          <UserPlus className="w-3 h-3" /> PROMOTE TO ADMIN
        </div>
        <p className="text-xs text-neutral-300 mb-2">Type an existing user's email — they'll get full Control Room access. They must already have an account.</p>
        <form onSubmit={promote} className="flex gap-2">
          <Input
            data-testid="promote-admin-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            type="email"
            className="bg-neutral-900 border-neutral-800 text-white rounded-none h-10"
          />
          <Button data-testid="promote-admin-btn" type="submit" disabled={busy || !email} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10 shrink-0">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Promote"}
          </Button>
        </form>
      </div>

      <div className="text-xs uppercase tracking-widest text-neutral-400 mb-2 flex items-center gap-2">
        <Star className="w-3 h-3 text-blue-500" /> Current admins ({admins.length})
      </div>

      {loading ? (
        <div className="text-neutral-400 flex items-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : admins.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-4 text-xs text-neutral-400 text-center">No admins yet.</div>
      ) : (
        <div className="divide-y divide-neutral-800 border border-neutral-800">
          {admins.map((a) => {
            const isSelf = a.user_id === currentUserId;
            const canRevoke = !a.is_super_admin && !isSelf;
            return (
              <div key={a.user_id} data-testid={`admin-row-${a.user_id}`} className="p-3 flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {a.name || a.email}
                    {a.is_super_admin && (
                      <span data-testid="super-admin-badge" className="text-[9px] uppercase tracking-widest bg-blue-500 text-white px-1.5 py-0.5">Primary</span>
                    )}
                    {isSelf && !a.is_super_admin && (
                      <span className="text-[9px] uppercase tracking-widest bg-neutral-800 text-neutral-300 px-1.5 py-0.5">You</span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 truncate">{a.email}</div>
                </div>
                {canRevoke ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`revoke-admin-${a.user_id}`}
                    onClick={() => revoke(a)}
                    className="text-neutral-400 hover:text-blue-500 uppercase tracking-wider font-display h-8"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Revoke
                  </Button>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest text-neutral-600">{a.is_super_admin ? "Locked" : "—"}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
